// apps/web/src/features/bookings/components/GuestCountSelector.tsx
import { useEffect, useCallback } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import { styled } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import PeopleIcon from '@mui/icons-material/People';

interface GuestCountSelectorProps {
  guestCount: number;
  onUpdateCount: (count: number) => void;
  minGuests?: number;
  maxGuests?: number;
}

const SelectorCard = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(4),
  backgroundColor: theme.palette.background.paper,
  borderRadius: theme.shape.borderRadius,
  border: `1px solid ${theme.palette.divider}`,
}));

const GuestCountDisplay = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: theme.spacing(3),
  marginTop: theme.spacing(3),
  marginBottom: theme.spacing(2),
}));

const CountNumber = styled(Typography)({
  fontSize: '3rem',
  fontWeight: 700,
  minWidth: 100,
  textAlign: 'center',
});

export function GuestCountSelector({
  guestCount,
  onUpdateCount,
  minGuests = 1,
  maxGuests = 100,
}: GuestCountSelectorProps) {
  // Validate props in development
  if (process.env.NODE_ENV === 'development') {
    if (minGuests < 0) {
      console.error('GuestCountSelector: minGuests cannot be negative');
    }
    if (maxGuests < minGuests) {
      console.error(`GuestCountSelector: maxGuests (${maxGuests}) < minGuests (${minGuests})`);
    }
    if (guestCount < minGuests || guestCount > maxGuests) {
      console.error(`GuestCountSelector: guestCount (${guestCount}) outside [${minGuests}, ${maxGuests}]`);
    }
  }

  // Clamp guestCount to valid range
  const clampedCount = Math.max(minGuests, Math.min(maxGuests, guestCount));

  // Notify parent if clamping occurred
  useEffect(() => {
    if (clampedCount !== guestCount) {
      onUpdateCount(clampedCount);
    }
  }, [clampedCount, guestCount, onUpdateCount]);

  const handleIncrement = useCallback(() => {
    if (clampedCount < maxGuests) {
      onUpdateCount(clampedCount + 1);
    }
  }, [clampedCount, maxGuests, onUpdateCount]);

  const handleDecrement = useCallback(() => {
    if (clampedCount > minGuests) {
      onUpdateCount(clampedCount - 1);
    }
  }, [clampedCount, minGuests, onUpdateCount]);

  return (
    <Box>
      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
        How many guests?
      </Typography>

      <SelectorCard>
        <PeopleIcon sx={{ fontSize: 64, color: 'primary.main', opacity: 0.5 }} />

        <GuestCountDisplay>
          <IconButton
            size="large"
            onClick={handleDecrement}
            disabled={clampedCount <= minGuests}
            aria-label="Decrease guest count"
            sx={{
              border: 2,
              borderColor: 'primary.main',
              '&:hover': {
                backgroundColor: 'primary.light',
              },
            }}
          >
            <RemoveIcon fontSize="large" />
          </IconButton>

          <CountNumber color="primary">{clampedCount}</CountNumber>

          <IconButton
            size="large"
            onClick={handleIncrement}
            disabled={clampedCount >= maxGuests}
            aria-label="Increase guest count"
            sx={{
              border: 2,
              borderColor: 'primary.main',
              '&:hover': {
                backgroundColor: 'primary.light',
              },
            }}
          >
            <AddIcon fontSize="large" />
          </IconButton>
        </GuestCountDisplay>

        <Typography variant="body2" color="text.secondary">
          {clampedCount === 1 ? '1 guest' : `${clampedCount} guests`}
        </Typography>
      </SelectorCard>
    </Box>
  );
}
